import {
	Plan,
	Product,
	ProductType,
	CustomerData,
	Subscription,
	BillingPeriod,
	PaymentMethod,
	PricePreviewInput,
	ProductInvoicePreview,
	EntitlementPlansResponse,
	EntitlementResponse,
	PlanTypes,
	NextPlanForEntitlement,
	EntitlementObject,
	FeatureTypes,
	UsageHistoryAnalytics,
	ProductAutomatedSubscriptionChangesLine,
} from '@/components/Billing/billing-types';
import { OrganizationSettings } from '@/react_app/components/BillingUsage/Settings/settings-types';
import {
	usePlans as usePlansGetter,
	useProduct as useProductGetter,
	usePlanById as usePlanByIdGetter,
	useProducts as useProductsGetter,
	useTeamPlans as useTeamPlansGetter,
	useCreditsPlans as useCreditsPlansGetter,
	useCustomerData as useCustomerDataGetter,
	useSubscriptions as useSubscriptionsGetter,
	useCustomerPlans as useCustomerPlansGetter,
	useBillingPeriod as useBillingPeriodGetter,
	useIsAnnuallyPlan as useIsAnnuallyPlanGetter,
	usePricingPreview as usePricingPreviewGetter,
	usePaymentMethods as usePaymentMethodsGetter,
	useAutomationPlans as useAutomationPlansGetter,
	usePlansByProductType as usePlansByProductTypeGetter,
	useCreditsSubscription as useCreditsSubscriptionGetter,
	useDefaultPaymentMethod as useDefaultPaymentMethodGetter,
	useOrganizationSettings as useOrganizationSettingsGetter,
	useAutomationSubscription as useAutomationSubscriptionGetter,
	useDefaultPaymentMethodId as useDefaultPaymentMethodIdGetter,
	useTeamFeaturesSubscription as useTeamFeaturesSubscriptionGetter,
	useSubscriptionByProductType as useSubscriptionByProductTypeGetter,
	useCustomerFeatureSubscription as useCustomerFeatureSubscriptionGetter,
	useEntitlements as useEntitlementsGetter,
	useEntitlementsLastRefresh as useEntitlementsLastRefreshGetter,
	useEntitlementsIsRefreshing as useEntitlementsIsRefreshingGetter,
	useEntitlement as useEntitlementGetter,
	useEntitlementPlans as useEntitlementPlansGetter,
	useUpNextRequiredPlanToUse as useUpNextRequiredPlanToUseGetter,
	useUpNextRequiredPlanToUseData as useUpNextRequiredPlanToUseDataGetter,
	useHasCapability as useHasCapabilityGetter,
	useFeatureUsageHistory as useFeatureUsageHistoryGetter,
	useFeatureUsageAnalyticsHistory as useFeatureUsageAnalyticsHistoryGetter,
	useProductAutomatedSubscriptionChanges as useProductAutomatedSubscriptionChangesGetter,
} from '@/store/modules/billing/getters';
import { useStore } from '@/utils/store';
export const useBillingGetters = () => {
	const { getter } = useStore();
	return {
		useTeamPlans: getter<Plan[]>(useTeamPlansGetter),
		usePlans: getter<Record<string, Plan>>(usePlansGetter),
		useCreditsPlans: getter<Plan[]>(useCreditsPlansGetter),
		useCustomerPlans: getter<Plan[]>(useCustomerPlansGetter),
		useIsAnnuallyPlan: getter<boolean>(useIsAnnuallyPlanGetter),
		billingPeriod: getter<BillingPeriod>(useBillingPeriodGetter),
		useCustomerData: getter<CustomerData>(useCustomerDataGetter),
		useAutomationPlans: getter<Plan[]>(useAutomationPlansGetter),
		useProducts: getter<Record<string, Product>>(useProductsGetter),
		usePlanById: getter<(planId: string) => Plan>(usePlanByIdGetter),
		useCreditsSubscription: getter<Subscription>(useCreditsSubscriptionGetter),
		useProduct: getter<(productType: ProductType) => Product>(useProductGetter),
		useDefaultPaymentMethod: getter<PaymentMethod>(useDefaultPaymentMethodGetter),
		useSubscriptions: getter<Record<string, Subscription>>(useSubscriptionsGetter),
		useAutomationSubscription: getter<Subscription>(useAutomationSubscriptionGetter),
		usePaymentMethods: getter<Record<string, PaymentMethod>>(usePaymentMethodsGetter),
		useDefaultPaymentMethodId: getter<string | null>(useDefaultPaymentMethodIdGetter),
		useTeamFeaturesSubscription: getter<Subscription>(useTeamFeaturesSubscriptionGetter),
		useOrganizationSettings: getter<OrganizationSettings>(useOrganizationSettingsGetter),
		useCustomerFeatureSubscription: getter<Subscription>(useCustomerFeatureSubscriptionGetter),
		usePlansByProductType: getter<(productType: string) => Plan[]>(usePlansByProductTypeGetter),
		useSubscriptionByProductType: getter<(productType: ProductType) => Subscription>(useSubscriptionByProductTypeGetter),
		usePricingPreview: getter<(pricePreviewInput: PricePreviewInput) => ProductInvoicePreview | undefined>(usePricingPreviewGetter),
		useEntitlement: getter<(featureId: string) => EntitlementObject | undefined>(useEntitlementGetter),
		useEntitlements: getter<() => EntitlementResponse | undefined>(useEntitlementsGetter),
		useEntitlementPlans: getter<() => EntitlementPlansResponse | undefined>(useEntitlementPlansGetter),
		useUpNextRequiredPlanToUse: getter<(featureId: string) => PlanTypes>(useUpNextRequiredPlanToUseGetter),
		useUpNextRequiredPlanToUseData: getter<(featureId: string) => NextPlanForEntitlement>(useUpNextRequiredPlanToUseDataGetter),
		useHasCapability: getter<(featureId: string) => boolean>(useHasCapabilityGetter),
		useFeatureUsageHistory: getter<(featureId: FeatureTypes) => unknown>(useFeatureUsageHistoryGetter),
		useFeatureUsageAnalyticsHistory: getter<(featureId: FeatureTypes) => UsageHistoryAnalytics>(useFeatureUsageAnalyticsHistoryGetter),
		useProductAutomatedSubscriptionChanges: getter<(productType: ProductType) => ProductAutomatedSubscriptionChangesLine>(
			useProductAutomatedSubscriptionChangesGetter
		),
		useEntitlementsLastRefresh: getter<Date>(useEntitlementsLastRefreshGetter),
		useEntitlementsIsRefreshing: getter<boolean>(useEntitlementsIsRefreshingGetter),
	};
};
